/**
 * EditEntityCommand - Generic command for editing entities (StepNode, StrategyBlock)
 *
 * Implements the Command pattern for undoable entity editing.
 * Delegates to FlowCoordinator for actual update logic.
 *
 * Generic Architecture:
 * - TEntity extends FlowEntity (StepNode, StrategyBlock)
 * - TService (StepNodeScreenService, StrategyBlockService)
 * - Uses ONLY generic coordinator methods (updateEntity, getAllEntities)
 *
 * Usage:
 * ```typescript
 * // Edit V2 StepNode
 * const command = new EditEntityCommand<FlowStepNode, StepNodeScreenService>(
 *   'screen-1',
 *   { name: 'Updated Name', struct: { ... } },
 *   screenContext
 * );
 *
 * // Edit block
 * const command = new EditEntityCommand<StrategyBlock, StrategyBlockService>(
 *   'block-1',
 *   { name: 'New Block Name', description: 'Updated description' },
 *   blockContext
 * );
 *
 * await commandManager.execute(command);
 * ```
 *
 * Related Requirements:
 * - FR-P1-003: Commands use coordinator ONLY for all operations
 * - FR-P3-001: Generic FlowCoordinator interface
 * - FR-P3-004: Generic CommandContext
 */

import { Command, CommandResult } from './Command';
import { CommandContext } from './CommandContext';
import type { FlowEntity } from '@modules/reactFlow/types/flow-canvas-types';

/**
 * EditEntityCommand - Generic command for editing flow entities
 *
 * @template TEntity - Entity type extending FlowEntity (StepNode, StrategyBlock)
 * @template TService - Firebase service type (StepNodeScreenService, StrategyBlockService)
 */
export class EditEntityCommand<
  TEntity extends FlowEntity,
  TService
> implements Command {
  public readonly id: string;
  public readonly description: string;
  public readonly timestamp: number;

  // Backup for undo
  private originalEntity?: TEntity;

  /**
   * @param entityId - ID of entity to edit
   * @param updates - Partial entity updates to apply
   * @param context - Command execution context
   */
  constructor(
    private entityId: string,
    private updates: Partial<TEntity>,
    private context: CommandContext<TEntity, TService>
  ) {
    this.id = `edit-entity-${entityId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    this.description = `Edit entity ${entityId}`;
    this.timestamp = Date.now();
  }

  /**
   * Execute command - Update entity via coordinator
   *
   * Flow:
   * 1. Backup original entity for undo
   * 2. Validate entity exists
   * 3. Call coordinator.updateEntity() - handles Registry + Canvas + Firebase
   * 4. Return success result
   *
   * @returns Promise resolving with command result
   */
  async execute(): Promise<CommandResult> {
    try {
      console.log(`[EditEntityCommand] Executing edit for entity: ${this.entityId}`);

      // Step 1: Backup original entity for undo (use direct lookup)
      this.originalEntity = this.context.coordinator.getEntity(this.entityId);

      if (!this.originalEntity) {
        throw new Error(`Entity not found: ${this.entityId}`);
      }

      // Step 2: Merge updates with original entity to get full entity
      const fullEntity = { ...this.originalEntity, ...this.updates } as TEntity;

      // Step 3: Update via coordinator's universal method
      await this.context.coordinator.updateEntityAndRefreshConnections(
        this.entityId,
        fullEntity
      );

      console.log(`[EditEntityCommand] ✅ Entity updated: ${this.entityId}`);

      return {
        success: true,
        affectedEntities: { screens: [this.entityId] }
      };
    } catch (error) {
      console.error('[EditEntityCommand] ❌ Edit failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Unknown error'),
        affectedEntities: {}
      };
    }
  }

  /**
   * Undo command - Restore original entity via coordinator
   *
   * Flow:
   * 1. Call coordinator.updateEntityAndRefreshConnections() with original entity data
   * 2. Return success result
   *
   * @returns Promise resolving with command result
   */
  async undo(): Promise<CommandResult> {
    if (!this.originalEntity) {
      return {
        success: false,
        error: new Error('No original entity to restore'),
        affectedEntities: {}
      };
    }

    try {
      console.log(`[EditEntityCommand] Undoing edit for entity: ${this.entityId}`);

      // Restore original entity via coordinator's universal method
      await this.context.coordinator.updateEntityAndRefreshConnections(
        this.entityId,
        this.originalEntity
      );

      console.log('[EditEntityCommand] ✅ Undo successful');

      return {
        success: true,
        affectedEntities: { screens: [this.entityId] }
      };
    } catch (error) {
      console.error('[EditEntityCommand] ❌ Undo failed:', error);
      return {
        success: false,
        error: error instanceof Error ? error : new Error('Undo failed'),
        affectedEntities: {}
      };
    }
  }

  /**
   * Check if command can be executed
   * @returns true if entity exists
   */
  canExecute(): boolean {
    return !!this.context.coordinator.getEntity(this.entityId);
  }

  /**
   * Check if command can be undone
   * @returns true if original entity was backed up
   */
  canUndo(): boolean {
    return !!this.originalEntity;
  }
}
